/**
 * @file DeliveryPickupPointCard.tsx
 * @description Карточка выбранного пункта выдачи в расчёте (phase19.12 шаг 5).
 *
 * Показывает адрес и график работы по коду ПВЗ и главное — совпадает ли пункт
 * с тем, который выбрал клиент на витрине: менеджер не должен случайно увезти
 * заказ в другой пункт.
 */

import { Loader2, MapPin } from "lucide-react";

import { usePickupPoint } from "./usePickupPoint";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";

interface Props {
  code: string;
  city?: string | null;
  /** ПВЗ из заказа — с ним сравниваем выбор менеджера. */
  clientCode?: string | null;
  onChangeClick: () => void;
  disabled?: boolean;
}

export function DeliveryPickupPointCard({
  code,
  city,
  clientCode,
  onChangeClick,
  disabled,
}: Props) {
  const { point, loading } = usePickupPoint(code || clientCode || null, city);
  const effectiveCode = code || clientCode || "";
  const sameAsClient = Boolean(clientCode) && effectiveCode === clientCode;

  return (
    <div className="flex flex-col gap-2 rounded-lg p-3 [box-shadow:inset_0_0_0_1px_var(--outline-variant)]">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-start gap-2">
          <MapPin className="mt-0.5 size-4 shrink-0 text-on-surface-variant" />
          <div className="min-w-0">
            <div className="text-sm font-medium">
              {effectiveCode ? point?.address ?? `Пункт ${effectiveCode}` : "Пункт не выбран"}
            </div>
            {loading ? (
              <div className="flex items-center gap-1 text-xs text-on-surface-variant">
                <Loader2 className="size-3 animate-spin" /> Ищем пункт в справочнике…
              </div>
            ) : null}
            {point?.workingHours ? (
              <div className="text-xs text-on-surface-variant">{point.workingHours}</div>
            ) : null}
            {effectiveCode ? (
              <div className="mt-1 font-mono text-[11px] uppercase text-on-surface-variant">
                {effectiveCode}
              </div>
            ) : null}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="min-h-11"
          onClick={onChangeClick}
          disabled={disabled}
        >
          {effectiveCode ? "Сменить ПВЗ" : "Выбрать ПВЗ"}
        </Button>
      </div>

      {effectiveCode ? (
        sameAsClient ? (
          <Badge variant="success" className="self-start">
            ✓ тот же, что выбрал клиент
          </Badge>
        ) : clientCode ? (
          <Badge variant="warning" className="self-start">
            Клиент выбирал {clientCode}
          </Badge>
        ) : null
      ) : (
        <p className="text-xs text-on-surface-variant">
          Клиент пункт не выбирал — укажите его сами, он сохранится в отгрузку.
        </p>
      )}
    </div>
  );
}
